import { useCallback, useRef } from 'react'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded'
import StopRoundedIcon from '@mui/icons-material/StopRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import type { EmotionResult } from '@/lib/emotion'
import { FA_COLOR, type EmotionKey, type Expressions } from './constants'
import EmotionStream from './EmotionStream'
import { useLiveEmotions, HISTORY_SECONDS } from './useLiveEmotions'

/** Non-neutral emotions, in the order the readout lists them. */
const EXPRESSIONS: EmotionKey[] = ['happy', 'surprised', 'sad', 'angry', 'disgusted', 'fearful']
/** Below this nothing is highlighted and the face reads as neutral. */
const LOW_T = 0.25
const STAGE = '#0E0F22'
const IDLE_GLOW = '#5A5470'

const glass = {
  background: 'rgba(14,15,34,0.55)',
  backdropFilter: 'blur(14px) saturate(160%)',
  WebkitBackdropFilter: 'blur(14px) saturate(160%)',
  border: '1px solid rgba(255,255,255,0.12)',
  color: '#fff',
  '@media (prefers-reduced-transparency: reduce)': { background: 'rgba(14,15,34,0.92)', backdropFilter: 'none' },
} as const

function mix(a: string, b: string, t: number) {
  const p = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16))
  const x = p(a)
  const y = p(b)
  return '#' + x.map((v, i) => Math.round(v + (y[i] - v) * t).toString(16).padStart(2, '0')).join('')
}

function strongest(e: Expressions | null): [EmotionKey, number] | null {
  if (!e) return null
  return EXPRESSIONS.reduce<[EmotionKey, number]>((b, k) => (e[k] > b[1] ? [k, e[k]] : b), ['happy', -1])
}

const pct = (v: number) => Math.round(v * 100)

/**
 * Live emotion demo: webcam + the in-browser engine (same model as studies,
 * nothing sent to the server). Camera on the left with a face reticle, the
 * strongest expression and all seven scores on the right, the last few
 * seconds as a stream underneath.
 */
export default function LiveEmotionDemo() {
  const { t } = useTranslation('liveDemo')
  const frameRef = useRef<HTMLDivElement | null>(null)
  const reticleRef = useRef<HTMLDivElement | null>(null)

  // Face reticle follows the detection box directly on the DOM — no render per frame.
  const placeReticle = useCallback((r: EmotionResult, video: HTMLVideoElement) => {
    const el = reticleRef.current
    const frame = frameRef.current
    if (!el || !frame) return
    if (!r.face_detected || !r.face_area || !video.videoWidth) {
      el.style.opacity = '0'
      return
    }
    const W = frame.clientWidth
    const H = frame.clientHeight
    const s = Math.max(W / video.videoWidth, H / video.videoHeight)
    const ox = (W - video.videoWidth * s) / 2
    const oy = (H - video.videoHeight * s) / 2
    const [x, y, w, h] = r.face_area
    const left = W - (ox + (x + w) * s) // mirrored, like the preview
    el.style.width = `${w * s}px`
    el.style.height = `${h * s}px`
    el.style.transform = `translate3d(${left}px, ${oy + y * s}px, 0)`
    el.style.opacity = '1'
  }, [])

  const { videoRef, historyRef, phase, latest, start, stop } = useLiveEmotions(placeReticle)
  const live = phase === 'live'
  const e = latest.e
  const top = strongest(e)
  const low = !top || top[1] < LOW_T
  const glow = top ? mix(IDLE_GLOW, FA_COLOR[top[0]], Math.min(1, top[1] / 0.5)) : IDLE_GLOW
  const errorKey = phase === 'denied' || phase === 'nocam' || phase === 'unsupported' ? phase : null

  return (
    <Box sx={{ maxWidth: 1240, mx: 'auto', width: '100%' }}>
      {/* Title */}
      <Box sx={{ mb: { xs: 2.5, md: 3 }, px: { xs: 0.5, md: 0 } }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: (th) => th.affexy.fonts.heading,
            fontWeight: 700,
            fontSize: { xs: 28, md: 36 },
            lineHeight: 1.08,
            letterSpacing: '-0.025em',
            color: 'text.primary',
          }}
        >
          {t('live.heading')}
        </Typography>
        <Typography sx={{ mt: 1, fontSize: 16, lineHeight: 1.5, color: (th) => th.affexy.colors.ink56, maxWidth: 640 }}>
          {t('live.sub')}
        </Typography>
      </Box>

      {/* Stage */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          isolation: 'isolate',
          borderRadius: { xs: '22px', md: '30px' },
          background: STAGE,
          color: '#fff',
          p: { xs: 1.5, sm: 2.5, md: 3.5 },
          boxShadow: '0 30px 80px -30px rgba(14,15,34,0.55)',
        }}
      >
        {/* Aura tinted by the strongest emotion */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: -1,
            pointerEvents: 'none',
            '& i': {
              position: 'absolute',
              borderRadius: '50%',
              filter: 'blur(110px)',
              backgroundColor: glow,
              opacity: live ? 0.55 : 0.3,
              transition: 'background-color 1.2s ease, opacity 1s ease',
            },
          }}
        >
          <i style={{ width: '55%', height: '70%', left: '-10%', top: '-20%' }} />
          <i style={{ width: '45%', height: '60%', right: '-12%', bottom: '-25%' }} />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.45fr) minmax(280px, 1fr)' },
            gap: { xs: 2.5, md: 4 },
            alignItems: 'center',
          }}
        >
          {/* Camera */}
          <Box
            ref={frameRef}
            sx={{
              position: 'relative',
              aspectRatio: '4 / 3',
              borderRadius: { xs: '16px', md: '22px' },
              overflow: 'hidden',
              background: 'radial-gradient(circle at 50% 38%, #24223a 0%, #111226 70%)',
              boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)',
            }}
          >
            <Box
              component="video"
              ref={videoRef}
              playsInline
              muted
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transform: 'scaleX(-1)',
                opacity: live ? 1 : 0,
                transition: 'opacity .6s ease',
              }}
            />

            {/* Face reticle */}
            <Box
              ref={reticleRef}
              aria-hidden
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                opacity: 0,
                pointerEvents: 'none',
                transition: 'transform 160ms linear, width 160ms linear, height 160ms linear, opacity .3s ease',
                '--c': low ? 'rgba(255,255,255,0.85)' : FA_COLOR[top![0]],
                '& span': {
                  position: 'absolute',
                  width: '18%',
                  height: '18%',
                  borderColor: 'var(--c)',
                  borderStyle: 'solid',
                  borderWidth: 0,
                  transition: 'border-color .4s ease',
                },
                '@media (prefers-reduced-motion: reduce)': { transition: 'opacity .2s ease' },
              }}
            >
              <span style={{ left: 0, top: 0, borderLeftWidth: 3, borderTopWidth: 3, borderTopLeftRadius: 14 }} />
              <span style={{ right: 0, top: 0, borderRightWidth: 3, borderTopWidth: 3, borderTopRightRadius: 14 }} />
              <span style={{ left: 0, bottom: 0, borderLeftWidth: 3, borderBottomWidth: 3, borderBottomLeftRadius: 14 }} />
              <span style={{ right: 0, bottom: 0, borderRightWidth: 3, borderBottomWidth: 3, borderBottomRightRadius: 14 }} />
            </Box>

            {live && (
              <>
                <Box
                  sx={{
                    ...glass,
                    position: 'absolute',
                    left: 14,
                    top: 14,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 99,
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: '0.01em',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: '#EA5048',
                      boxShadow: '0 0 0 4px rgba(234,80,72,0.25)',
                      animation: 'affexy-pulse 1.6s ease-in-out infinite',
                      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                    }}
                  />
                  {t('live.badge')}
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={stop}
                  aria-label={t('live.stop')}
                  sx={{
                    ...glass,
                    position: 'absolute',
                    right: 14,
                    top: 14,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    pl: 1.25,
                    pr: 1.75,
                    py: 0.75,
                    borderRadius: 99,
                    font: 'inherit',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'transform 100ms ease-out, background-color .2s',
                    '&:hover': { background: 'rgba(14,15,34,0.72)' },
                    '&:active': { transform: 'scale(0.96)' },
                    '&:focus-visible': { outline: '2px solid #fff', outlineOffset: 2 },
                  }}
                >
                  <StopRoundedIcon sx={{ fontSize: 18 }} />
                  {t('live.stop')}
                </Box>
                <Box
                  sx={{
                    ...glass,
                    position: 'absolute',
                    left: '50%',
                    bottom: 18,
                    transform: `translate(-50%, ${latest.face ? 8 : 0}px)`,
                    opacity: latest.face ? 0 : 1,
                    transition: 'opacity .3s ease, transform .3s ease',
                    px: 2,
                    py: 1,
                    borderRadius: 99,
                    fontSize: 14,
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                  }}
                >
                  {t('live.noFace')}
                </Box>
              </>
            )}

            {/* Gate: idle / starting / error */}
            {!live && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'grid',
                  placeContent: 'center',
                  justifyItems: 'center',
                  textAlign: 'center',
                  gap: 2,
                  p: 3,
                }}
              >
                {phase === 'starting' ? (
                  <>
                    <CircularProgress size={28} thickness={3} sx={{ color: 'rgba(255,255,255,0.85)' }} />
                    <Typography sx={{ fontSize: 15, color: 'rgba(255,255,255,0.7)' }}>{t('live.starting')}</Typography>
                  </>
                ) : (
                  <>
                    <Box
                      sx={{
                        ...glass,
                        width: 72,
                        height: 72,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        mb: 0.5,
                      }}
                    >
                      <VideocamRoundedIcon sx={{ fontSize: 32 }} />
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: (th) => th.affexy.fonts.heading,
                        fontWeight: 700,
                        fontSize: { xs: 20, md: 24 },
                        letterSpacing: '-0.015em',
                        lineHeight: 1.2,
                      }}
                    >
                      {errorKey ? t(`live.error.${errorKey}.title`) : t('live.idle.title')}
                    </Typography>
                    <Typography sx={{ fontSize: 15, lineHeight: 1.5, color: 'rgba(255,255,255,0.66)', maxWidth: 380 }}>
                      {errorKey ? t(`live.error.${errorKey}.sub`) : t('live.idle.sub')}
                    </Typography>
                    <Button
                      variant="contained"
                      disableElevation
                      onClick={start}
                      startIcon={<VideocamRoundedIcon />}
                      sx={{
                        mt: 1,
                        px: 3,
                        py: 1.25,
                        borderRadius: 99,
                        fontSize: 15,
                        fontWeight: 600,
                        bgcolor: '#EA5048',
                        '&:hover': { bgcolor: '#C93A35' },
                        transition: 'transform 100ms ease-out, background-color .2s',
                        '&:active': { transform: 'scale(0.97)' },
                      }}
                    >
                      {errorKey ? t('live.retry') : t('live.idle.start')}
                    </Button>
                  </>
                )}
              </Box>
            )}
          </Box>

          {/* Readout */}
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, py: { md: 1 } }}>
            <Typography
              sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}
            >
              {t('live.now')}
            </Typography>

            <Box sx={{ mt: 1.25, minHeight: { xs: 112, md: 136 } }}>
              <Typography
                sx={{
                  fontFamily: (th) => th.affexy.fonts.heading,
                  fontWeight: 700,
                  fontSize: { xs: 26, md: 30 },
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1,
                  color: low ? 'rgba(255,255,255,0.92)' : FA_COLOR[top![0]],
                  transition: 'color .4s ease',
                }}
              >
                {!live || !e ? '—' : low ? t('live.calm') : t(`emotions.${top![0]}`)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mt: 0.5 }}>
                <Typography
                  sx={{
                    fontFamily: (th) => th.affexy.fonts.heading,
                    fontWeight: 300,
                    fontSize: { xs: 64, md: 84 },
                    lineHeight: 1,
                    letterSpacing: '-0.045em',
                    fontVariantNumeric: 'tabular-nums',
                    color: '#fff',
                    opacity: live && e ? 1 : 0.2,
                    transition: 'opacity .4s ease',
                  }}
                >
                  {live && e ? pct(low ? e.neutral : top![1]) : 0}
                  <Box component="span" sx={{ fontSize: '0.42em', fontWeight: 400, ml: 0.5, letterSpacing: 0, color: 'rgba(255,255,255,0.55)' }}>
                    %
                  </Box>
                </Typography>
                {live && e && (
                  <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', lineHeight: 1.4 }}>
                    {low
                      ? t('live.neutralHint', { pct: pct(e.neutral) })
                      : t('live.neutralAlongside', { pct: pct(e.neutral) })}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ height: '1px', bgcolor: 'rgba(255,255,255,0.1)', my: { xs: 2, md: 2.25 } }} />

            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: { xs: 1.25, md: 1.4 } }}>
              {[...EXPRESSIONS, 'neutral' as EmotionKey].map((k) => {
                const v = live && e ? e[k] : 0
                const on = live && !low && top?.[0] === k
                return (
                  <Box component="li" key={k} sx={{ opacity: live ? (on || v >= 0.06 ? 1 : 0.55) : 0.4, transition: 'opacity .4s ease' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                      <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: FA_COLOR[k], flex: 'none' }} />
                      <Typography sx={{ fontSize: 14, fontWeight: on ? 700 : 500, color: on ? '#fff' : 'rgba(255,255,255,0.78)', flex: 1 }}>
                        {t(`emotions.${k}`)}
                      </Typography>
                      <Typography
                        sx={{ fontSize: 14, fontVariantNumeric: 'tabular-nums', fontWeight: on ? 700 : 500, color: on ? '#fff' : 'rgba(255,255,255,0.6)' }}
                      >
                        {live && e ? `${pct(v)}%` : '–'}
                      </Typography>
                    </Box>
                    <Box sx={{ height: 4, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                      <Box
                        sx={{
                          height: '100%',
                          width: '100%',
                          borderRadius: 2,
                          bgcolor: FA_COLOR[k],
                          transformOrigin: 'left center',
                          transform: `scaleX(${v})`,
                          transition: 'transform 280ms cubic-bezier(.2,.8,.2,1)',
                          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                        }}
                      />
                    </Box>
                  </Box>
                )
              })}
            </Box>
          </Box>
        </Box>

        {/* Stream */}
        <Box sx={{ mt: { xs: 2.5, md: 3.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>
              {t('live.stream', { s: HISTORY_SECONDS })}
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{t('live.streamNow')}</Typography>
          </Box>
          <EmotionStream historyRef={historyRef} active={live} top={!low && top ? top[0] : null} height={110} />
        </Box>
      </Box>

      {/* Privacy note */}
      <Box
        sx={{
          mt: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          color: (th) => th.affexy.colors.ink56,
          fontSize: 13.5,
          textAlign: 'center',
          px: 2,
        }}
      >
        <LockRoundedIcon sx={{ fontSize: 16, flex: 'none' }} />
        <span>{t('live.privacy')}</span>
      </Box>
    </Box>
  )
}
